import { Button } from '@heidi/design-system/button';
import { Link } from '@heidi/design-system/link';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  TypographyP,
  TypographyP2SemiBold,
  typographyVariant,
} from '@heidi/design-system/typography';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { Clock10 } from 'lucide-react';
import { type Ref } from 'react';
import { FormattedMessage } from 'react-intl';
import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import { useTrialRecapModal } from '../hooks/useTrialRecapModal';
import { TeamTrialUpgradeRequestButton } from '../team-trial-upgrade-request-button/team-trial-upgrade-request-button';
import { trialUpgradeRequestCallbackModalMessages } from '../trial-upgrade-request-callback-modal/messages';
import { getCommerceTrialsUiPorts } from '../trials-ui-ports';
import { trialRecapModalMessages } from './messages';
import { TrialRecapFeatureList } from './trial-recap-feature-list';

/**
 * Trial recap modal shown near the end of a Clinician (or Practice) trial to
 * summarise how the user has engaged with premium Evidence features and prompt
 * them to upgrade (or contact sales when the viewer is a team admin on a
 * non–Stripe-managed team).
 *
 * Self-contained: open state is owned by {@link trialRecapModalOpenAtom}, and
 * trial/plan context is sourced from {@link useReverseTrial} and
 * {@link useQueryUserTier} via {@link useTrialRecapModal}. Mounted globally in `GlobalModalsWrapper` behind
 * `IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED`. The team upgrade request subflow uses
 * {@link openTeamTrialUpgradeRequestModalAtom} and the globally mounted team trial
 * upgrade request modal.
 */
export const TrialRecapModal = () => {
  const {
    open,
    setOpen,
    upgradeButtonRef,
    daysLeft,
    isClinicianPlan,
    tierName,
    TierIcon,
    titleMessage,
    descriptionMessage,
    upgradeCtaMessage,
    planChangesArticleUrl,
    showNonAdminRequestBanner,
    showContactSalesInsteadOfUpgrade,
    handleUpgrade,
    handleContactSalesClick,
    handleRemindMeLater,
    openTeamTrialUpgradeRequestModal,
    hasCallbackRequested,
  } = useTrialRecapModal();

  if (getCommerceBillingUiPorts().isAppleCompliantBuild()) {
    return null;
  }

  return (
    <DialogDrawer open={open} onOpenChange={setOpen}>
      <DialogDrawerContent
        data-testid="trial-recap-modal"
        className="gap-6 border-border bg-surface p-8 sm:max-w-[494px] sm:rounded-2xl"
        onOpenAutoFocus={(e) => {
          // Avoid focusing the first focusable element in the modal content
          // (e.g. `StackedSourceAvatars`). Prefer the primary CTA.
          e.preventDefault();
          upgradeButtonRef.current?.focus();
        }}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-2">
            <div
              aria-hidden
              className={cn(
                'flex size-12 items-center justify-center rounded-md',
                isClinicianPlan ? 'bg-tier-pro-muted' : 'bg-tier-practice-muted'
              )}
            >
              <Clock10
                className={cn(
                  'size-6 stroke-[1.5]',
                  isClinicianPlan ? 'text-tier-pro-text' : 'text-tier-practice-text'
                )}
              />
            </div>
            <TypographyP2SemiBold
              className={cn(
                'text-center leading-none',
                isClinicianPlan ? 'text-tier-pro-text' : 'text-tier-practice-text'
              )}
            >
              <FormattedMessage
                {...trialRecapModalMessages.daysLeft}
                values={{ daysLeft }}
              />
            </TypographyP2SemiBold>
          </div>

          <DialogDrawerTitle
            className={cn(typographyVariant({ variant: 'h2' }), 'text-center')}
          >
            <FormattedMessage {...titleMessage} values={{ tierName }} />
          </DialogDrawerTitle>

          <TypographyP className="text-center text-text-secondary">
            <FormattedMessage
              {...descriptionMessage}
              values={{
                daysLeft,
                link: (chunks) => (
                  <Link
                    variant="inline"
                    tone="default"
                    href={planChangesArticleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </Link>
                ),
              }}
            />
          </TypographyP>
        </div>

        <TrialRecapFeatureList />

        {!getCommerceBillingUiPorts().isAppleCompliantBuild() && (
          <div className="flex flex-col items-center gap-2">
            {showNonAdminRequestBanner ? (
              <TeamTrialUpgradeRequestButton
                ref={upgradeButtonRef as Ref<HTMLButtonElement>}
                plan={
                  isClinicianPlan
                    ? SubscriptionTier.CLINICIAN_PRO
                    : SubscriptionTier.CLINICIAN_PRO_PLUS
                }
                onOpenTeamRequestModal={() => {
                  setOpen(false);
                  openTeamTrialUpgradeRequestModal({
                    plan: isClinicianPlan
                      ? SubscriptionTier.CLINICIAN_PRO
                      : SubscriptionTier.CLINICIAN_PRO_PLUS,
                    trackRequestSubmitted:
                      getCommerceTrialsUiPorts().analytics
                        .trackNinetyDayTrialRecapTeamUpgradeRequestSubmit,
                    onRequestSent: () => {
                      setOpen(false);
                    },
                    onUserDismiss: () => {
                      setOpen(true);
                    },
                  });
                }}
              />
            ) : showContactSalesInsteadOfUpgrade ? (
              <Button
                ref={upgradeButtonRef as Ref<HTMLButtonElement>}
                type="button"
                variant={isClinicianPlan ? 'pro' : 'pro_plus'}
                size="lg"
                disabled={hasCallbackRequested}
                onClick={handleContactSalesClick}
                className="w-full"
              >
                {!hasCallbackRequested && <TierIcon />}
                <FormattedMessage
                  {...(hasCallbackRequested
                    ? trialUpgradeRequestCallbackModalMessages.callbackRequestedCta
                    : isClinicianPlan
                      ? trialRecapModalMessages.talkToUsKeepPlanCtaClinician
                      : trialRecapModalMessages.talkToUsKeepPlanCtaPractice)}
                />
              </Button>
            ) : (
              <Button
                ref={upgradeButtonRef as Ref<HTMLButtonElement>}
                type="button"
                variant={isClinicianPlan ? 'pro' : 'pro_plus'}
                size="lg"
                onClick={handleUpgrade}
                className="w-full"
              >
                <TierIcon />
                <FormattedMessage {...upgradeCtaMessage} />
              </Button>
            )}
            {!showNonAdminRequestBanner ? (
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleRemindMeLater}
                className="w-full"
              >
                <FormattedMessage {...trialRecapModalMessages.remindMeLater} />
              </Button>
            ) : null}
          </div>
        )}
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
